/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
const NEVER_RESOLVES = () => new Promise(() => {
    // intentionally never settles
});
const REJECTS = () => Promise.reject(new Error('Mocked query failure'));
/**
 * Builds a QueryClient that never reaches the network. Per seed:
 * - success: data is written straight into the cache.
 * - loading: a prefetch that never settles keeps the query in-flight; the mounting observer joins it.
 * - error: a rejecting prefetch puts the query in error; retryOnMount=false stops the observer refetching.
 */
export function buildMockQueryClient(seeds) {
    const qc = new QueryClient({
        defaultOptions: {
            queries: { retry: false, retryOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false }
        }
    });
    seeds.forEach(({ queryKey, scenario }) => {
        if (scenario.status === 'success') {
            qc.setQueryData(queryKey, scenario.data);
        }
        else if (scenario.status === 'loading') {
            void qc.prefetchQuery({ queryKey, queryFn: NEVER_RESOLVES });
        }
        else {
            void qc.prefetchQuery({ queryKey, queryFn: REJECTS });
        }
    });
    return qc;
}
function MockQueryClientProvider({ seeds, children }) {
    // Fresh client per story render so state never leaks between stories.
    const [client] = useState(() => buildMockQueryClient(seeds));
    return React.createElement(QueryClientProvider, { client: client }, children);
}
export function withMockQueryClient(seeds) {
    return function MockQueryClientDecorator(Story) {
        return (React.createElement(MockQueryClientProvider, { seeds: seeds },
            React.createElement(Story, null)));
    };
}
